import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Box,
  AppBar,
  Toolbar,
  Button,
  Typography,
} from '@mui/material';
import { Home, Lightbulb, Flask, Compass, Seedling } from '@orbit/ui';
import { Stage } from '@orbit/shared/constants';

interface NavItem {
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  stage?: Stage;
}

const navItems: NavItem[] = [
  { label: 'Home', icon: Home, path: '/home' },
  { label: 'Explore', icon: Lightbulb, path: '/explore', stage: Stage.Explore },
  { label: 'Incubation', icon: Flask, path: '/incubation', stage: Stage.Incubation },
  { label: 'Launch', icon: Compass, path: '/launch', stage: Stage.Launch },
  { label: 'Growth', icon: Seedling, path: '/growth', stage: Stage.Growth },
];

const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine which nav item is currently active based on the current path
  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const activeIndex = navItems.findIndex((item) => currentPath.startsWith(item.path));
    return activeIndex !== -1 ? activeIndex : 0; // Default to Home if no match
  };

  const handleNavigation = (event: React.SyntheticEvent, newValue: number) => {
    const selectedItem = navItems[newValue];
    if (selectedItem) {
      navigate(selectedItem.path);
    }
  };

  const handleButtonClick = (path: string) => {
    navigate(path);
  };

  const isCurrentPath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop Navigation - Top Bar */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'none', md: 'block' },
          bgcolor: '#2A2D4A', // Same as bg-background-surface
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          zIndex: 100,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 4 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              fontFamily: 'heading',
            }}
          >
            Orbit
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                onClick={() => handleButtonClick(item.path)}
                startIcon={<item.icon style={{ width: 20, height: 20 }} />}
                sx={{
                  color: isCurrentPath(item.path) ? 'primary.main' : 'text.secondary',
                  bgcolor: isCurrentPath(item.path) ? 'rgba(36, 137, 249, 0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(36, 137, 249, 0.1)',
                  },
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: isCurrentPath(item.path) ? 600 : 400,
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation - Bottom Bar */}
      <Paper
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderRadius: '12px 12px 0 0',
          maxWidth: 600,
          mx: 'auto',
          boxShadow: 3,
          bgcolor: '#2A2D4A', // Same as bg-background-surface
        }}
        elevation={3}
      >
        <Box
          sx={{
            overflowX: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          <BottomNavigation
            showLabels
            value={getCurrentValue()}
            onChange={handleNavigation}
            sx={{
              display: 'inline-flex',
              justifyContent: 'flex-start',
              bgcolor: '#2A2D4A',
              borderRadius: '12px 12px 0 0',
              width: '100%',
              minHeight: 64,
              marginTop: '8px',
            }}
          >
            {navItems.map((item, idx) => (
              <BottomNavigationAction
                key={item.label}
                label={item.label}
                icon={<item.icon style={{ width: 28, height: 28 }} />}
                sx={{
                  minWidth: 80,
                  '& .MuiBottomNavigationAction-label': {
                    marginTop: '6px',
                  },
                }}
              />
            ))}
          </BottomNavigation>
        </Box>
      </Paper>
    </>
  );
};

export default BottomNav;
